// PayoutList.js
// Team-level Payout page (each team = a CPO). Pick a year and month, choose which
// chargers to include (all by default), generate the payout, and download it as a
// styled Excel or branded PDF. Team totals + per-charger breakdown.
//
// A super admin can also select several teams — or all of a company's — in one go.
// Each team is fetched from its own per-team endpoint and stays a separate report:
// its own section on screen, its own sheet pair in Excel, its own pages in the PDF.
// Nothing is ever summed across teams. A payout is a per-team contract with its own
// payout rule, commission rate and GST, so a combined total would belong to no
// contract and could not be reconciled against any one CPO's invoice.
// The actions that act on a specific team — finalize, per-charger override, email,
// schedules, service-fee invoice — are single-team only and hide when several are
// selected.
import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Checkbox,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PostAddIcon from "@mui/icons-material/PostAdd";
import PaidIcon from "@mui/icons-material/Paid";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EmailIcon from "@mui/icons-material/Email";
import ScheduleIcon from "@mui/icons-material/Schedule";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx-js-style";
import Page from "src/components/Page";
import Token from "src/util/Token";
import {
  getTeamPayoutReport,
  finalizePayoutUrl,
  overridePayoutUrl,
  emailPayoutUrl,
  payoutEmailSchedulesUrl,
  payoutEmailScheduleUrl,
  serviceInvoicesUrl,
  serviceInvoiceByPeriodUrl,
  serviceInvoiceUrl,
  serviceInvoicePaidUrl,
} from "src/react-query/endPoints";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useList } from "src/hooks/useFetch.hook";
import { useDomainConfig } from "src/hooks/useDomainConfig";
import { useIsGrandParent } from "src/hooks/useIsGrandParent";
import { useIsRootSuperAdmin } from "src/hooks/useIsRootSuperAdmin";
import { useAccess } from "src/hooks/useAccess.hook";
import { FEATURE } from "src/util/enums";
import { isAmikPayoutDomain } from "src/components/charger/charger.constants";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import {
  S,
  withComma,
  fmtTime,
  buildChargingSummary,
  buildPayoutSummaryRows,
  buildPaymentTypeSections,
  sumOverDelivered,
  loadLogoDataUrl,
  formatPayoutRule,
} from "./payoutShared";
import { downloadServiceFeeInvoice, buildInvoicePayload } from "./serviceFeeInvoice";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Transaction detail columns, ordered for CPO visibility: identity -> timing ->
// energy -> money.
const TXN_COLUMNS = [
  { key: "chargerId", label: "Charger" },
  { key: "connectorId", label: "Connector" },
  { key: "transactionId", label: "Transaction ID" },
  { key: "paymentId", label: "Payment ID" },
  { key: "paymentType", label: "Type" },
  { key: "startTime", label: "Start Time", time: true },
  { key: "stopTime", label: "Stop Time", time: true },
  { key: "kWh", label: "kWh Consumed", num: true },
  { key: "pricePerKw", label: "Price/kWh (incl. GST)", money: true },
  { key: "chargingCost", label: "Charging Cost (incl. GST)", money: true },
  { key: "taxAmount", label: "GST", money: true },
  { key: "pgCharge", label: "Payment Gateway Fee", money: true },
  { key: "amikFee", label: "Amik CMS Fee (incl. GST)", money: true },
  { key: "payout", label: "Payout (incl. GST)", money: true },
  {
    key: "overDeliveredAmount",
    label: "Over-delivery (Adj.)",
    money: true,
    blankZero: true,
  },
];

const TXN_SCREEN_LIMIT = 500;

// A whole-company run can be dozens of teams, and each payout report is a heavy
// per-charger transaction query. Fire them a few at a time so one click doesn't
// stampede the API. Results come back in the order the items went in.
const TEAM_FETCH_CONCURRENCY = 4;

// Sentinel value for the "select all / clear" row inside the Team dropdown. It is
// never a real team id, so it can be stripped from the selection on the way in.
const ALL_TEAMS = "__all_teams__";

// Up to this many teams open expanded; beyond it every section starts collapsed.
const AUTO_EXPAND_LIMIT = 3;

// One collapsible team in a multi-team run.
//
// `unmountOnExit` is the whole point, not a nicety: a whole-company run can be
// dozens of teams, and mounting every per-charger table and 500-row transaction
// table at once is tens of thousands of DOM nodes in a single pass. Collapsed
// sections render nothing at all.
//
// The header therefore has to be worth reading on its own — chargers, kWh, fee
// and payout live there, so the common case (scan the company, open the one team
// that looks wrong) needs no expansion at all.
function TeamSection({ rep, expanded, onToggle, txnMode = false, children }) {
  const d = rep.data || {};
  const stat = (label, value) => (
    <Box sx={{ textAlign: "right", minWidth: 88 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  );
  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      TransitionProps={{ unmountOnExit: true }}
      sx={{ mb: 1, "&:before": { display: "none" } }}
      variant="outlined"
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          alignItems="center"
          sx={{ width: "100%", pr: 2, gap: 2, flexWrap: "wrap" }}
        >
          <Typography sx={{ fontWeight: 600, flexGrow: 1, minWidth: 160 }}>
            {rep.teamName}
          </Typography>
          {txnMode ? (
            stat("Transactions", d.transactions?.length ?? 0)
          ) : (
            <>
              {stat("Chargers", d.chargers?.length ?? 0)}
              {stat("kWh", withComma(d.summary?.totalKWh))}
              {stat("CMS Fee", withComma(d.payout?.amikFee))}
              {stat("Payout", withComma(d.payout?.payout))}
            </>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

const settleInBatches = async (items, size, fn) => {
  const out = [];
  for (let i = 0; i < items.length; i += size) {
    // eslint-disable-next-line no-await-in-loop
    out.push(...(await Promise.allSettled(items.slice(i, i + size).map(fn))));
  }
  return out;
};

export default function PayoutList() {
  const { teamId, team } = useSelectedTeam();
  const contextTeamId =
    teamId && typeof teamId === "object" ? teamId.id : teamId;
  const domainConfig = useDomainConfig();
  const brand = domainConfig?.name || "Amik";
  const isGrandParent = useIsGrandParent();
  const isRootSuperAdmin = useIsRootSuperAdmin();
  const payoutViewAccess = useAccess(FEATURE.PAYOUT, "V");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Same rule the sidebar applies, so the tab and the route agree: the ROOT
  // super admin is unlimited, a company-level user on an Amik-branded host keeps
  // the access they have today, and a limited super admin keeps Payout unless
  // their role explicitly denies it — a Payout row set to "N". No row at all
  // means the feature does not exist to be granted yet, which must not read as
  // a denial. Without this the hidden tab would still be one URL away.
  const payoutAccessRow = (team?.access ?? []).find(
    (item) => item.featureName === FEATURE.PAYOUT
  );
  const payoutExplicitlyDenied =
    !!payoutAccessRow && payoutAccessRow.view !== "Y";
  const canViewPayout =
    isRootSuperAdmin ||
    payoutViewAccess ||
    (isAmikPayoutDomain() && !isGrandParent) ||
    (isGrandParent && !payoutExplicitlyDenied);

  // Editing a payout — finalize, per-charger override, email/schedule, and the
  // whole invoice ledger (save, edit number, mark paid) — is a super-admin (GP)
  // capability, and within that it is role-controlled with a DEFAULT-ALLOW policy:
  // the ROOT owner is always unlimited, and any other GP super admin may edit
  // unless their role EXPLICITLY revokes it by setting Payout → Update = "N". A
  // missing Payout row, or any value other than "N", keeps edit — so no existing
  // admin loses edit until a role is deliberately set to read-only. The server
  // enforces the same via requirePayoutEditAccess. A team admin with only Payout
  // "V" (or any non-GP viewer) gets a read-only view. Every mutation control is
  // gated on this single flag so the policy lives in one place.
  const payoutEditDenied = payoutAccessRow?.update === "N";
  const canEdit = isRootSuperAdmin || (isGrandParent && !payoutEditDenied);
  const readOnly = canViewPayout && !canEdit;

  useEffect(() => {
    if (!canViewPayout) {
      dispatch(
        alertSlice.actions.setAlert({ open: true, message: "Not Authorized" })
      );
      navigate("/dashboard");
    }
  }, [canViewPayout, dispatch, navigate]);

  // GP (super admin) has access to every company, so the Payout screen lets them
  // drill Company → Team → chargers instead of using the top-right team selector.
  // Everyone else keeps using the selected team from context.
  const [gpCompany, setGpCompany] = useState("");
  // Teams are multi-select so a super admin can run one team, a few, or the whole
  // company in one go; the reports come back per team and are pooled into a single
  // merged total. Everyone else stays pinned to their context team.
  const [gpTeamIds, setGpTeamIds] = useState([]);
  const { data: companies = [] } = useList("companyList", isGrandParent);
  const { data: teamOptions = [] } = useList(
    "teams",
    isGrandParent && !!gpCompany,
    gpCompany
  );
  const selectedTeamIds = useMemo(
    () => (isGrandParent ? gpTeamIds : contextTeamId ? [contextTeamId] : []),
    [isGrandParent, gpTeamIds, contextTeamId]
  );
  const teamSelectionKey = selectedTeamIds.join(",");
  const isMultiTeam = selectedTeamIds.length > 1;
  const allTeamsSelected =
    teamOptions.length > 0 && gpTeamIds.length === teamOptions.length;
  const teamNameById = useMemo(
    () =>
      Object.fromEntries((teamOptions || []).map((t) => [String(t.teamId), t.name])),
    [teamOptions]
  );
  const teamLabel = (tid) =>
    teamNameById[String(tid)] ||
    (String(tid) === String(contextTeamId) && team?.name) ||
    `Team ${tid}`;
  // Every per-team action — charger filter, finalize, per-charger override, email,
  // schedules, service-fee invoice — needs exactly one team. In multi-team mode
  // there isn't one, so this stays null and those paths switch off rather than
  // silently acting on whichever team happens to be first.
  const resolvedTeamId = selectedTeamIds.length === 1 ? selectedTeamIds[0] : null;
  // Finalize, per-charger override, email/schedules and the service-fee invoice
  // all act on one team. They stay hidden for a pooled multi-team report; the
  // Ledger tab is cross-team by nature and keeps plain `canEdit`.
  const canEditThisReport = canEdit && !isMultiTeam;

  const { data: chargers = [], isFetching: chargersLoading } = useList(
    "chargers",
    isGrandParent ? !!resolvedTeamId : undefined,
    isGrandParent ? resolvedTeamId : undefined
  );

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  // Default selection: last (previous completed) month.
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const defaultYear = lastMonthDate.getFullYear();
  const defaultMonth = lastMonthDate.getMonth() + 1;

  const [year, setYear] = useState(defaultYear);
  const [month, setMonth] = useState(defaultMonth);
  const [selectedChargers, setSelectedChargers] = useState([]);
  // One entry per generated team ({ teamId, teamName, data }), in the order the
  // teams were selected. A payout is a per-team contract — its own payout rule,
  // commission rate and GST — so teams are never pooled into a single set of
  // totals. Each keeps its own summary, per-charger breakdown and transactions.
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Teams whose report failed during a multi-team run. The merged total is shown
  // without them, so they must be named — a payout that quietly drops a CPO is
  // worse than one that refuses to load.
  const [failedTeams, setFailedTeams] = useState([]);
  const [tab, setTab] = useState("summary");
  // Which team sections are open. A handful of teams open by default — expanding
  // three accordions by hand to read a three-team run is pointless friction — but
  // a whole-company run stays collapsed, which is what keeps it fast.
  const [expandedTeams, setExpandedTeams] = useState([]);
  const toggleTeam = (teamId) =>
    setExpandedTeams((prev) =>
      prev.includes(teamId)
        ? prev.filter((t) => t !== teamId)
        : [...prev, teamId]
    );

  // The per-team mutations (finalize, per-charger override, email, service-fee
  // invoice) act on one team's report, so they read the sole report and stay
  // unavailable while several teams are on screen.
  const data = reports.length === 1 ? reports[0].data : null;
  const hasReports = reports.length > 0;
  const isMultiReport = reports.length > 1;
  // Tab badge only — a count of rows on screen, not a payout figure being pooled.
  const totalTxnCount = reports.reduce(
    (a, r) => a + (r.data?.transactions?.length || 0),
    0
  );

  // Company total across the selected teams — shown alongside, never instead of,
  // the per-team sections below. Only money and counts are added up. Averages,
  // payout rule, commission rate and GST are deliberately left out: each team is a
  // separate contract, so a blended rate would describe none of them. Currency is
  // checked rather than assumed, because adding across currencies is meaningless.
  const r2 = (n) => Number((Number(n) || 0).toFixed(2));
  const companyTotals = useMemo(() => {
    if (reports.length < 2) return null;
    const sum = (pick) =>
      reports.reduce((a, r) => a + (Number(pick(r.data)) || 0), 0);
    const currencies = [
      ...new Set(reports.map((r) => r.data?.meta?.currency || "INR")),
    ];
    return {
      teams: reports.length,
      chargers: reports.reduce((a, r) => a + (r.data?.chargers?.length || 0), 0),
      currency: currencies[0],
      mixedCurrency: currencies.length > 1,
      totalTransactions: sum((d) => d.summary?.totalTransactions),
      totalKWh: r2(sum((d) => d.summary?.totalKWh)),
      totalConsumptionCost: r2(sum((d) => d.summary?.totalConsumptionCost)),
      totalTaxAmount: r2(sum((d) => d.summary?.totalTaxAmount)),
      totalChargingCost: r2(sum((d) => d.summary?.totalChargingCost)),
      totalCollectionByAmik: r2(sum((d) => d.payout?.totalCollectionByAmik)),
      amikFee: r2(sum((d) => d.payout?.amikFee)),
      payout: r2(sum((d) => d.payout?.payout)),
      payoutExclGst: r2(sum((d) => d.payout?.payoutExclGst)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports]);

  // Default all chargers selected, re-defaulting whenever the selected team changes.
  // Keyed on the team we last initialised for (not a run-once flag), so switching
  // teams from the top-right dropdown refreshes the charger list. Guarded by
  // !chargersLoading so we seed from the new team's chargers, not the previous
  // team's stale list; the ref guard keeps a user's manual de-selection intact
  // across unrelated re-renders (chargers is a fresh array ref each render).
  const initedTeamRef = useRef(null);
  useEffect(() => {
    if (
      !chargersLoading &&
      chargers.length &&
      initedTeamRef.current !== resolvedTeamId
    ) {
      initedTeamRef.current = resolvedTeamId;
      setSelectedChargers(chargers.map((c) => c.chargepoint_pk));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargers, chargersLoading, resolvedTeamId]);

  // On team switch, immediately drop the previous team's selection and any report
  // it generated so nothing stale lingers while the new charger list loads.
  const prevTeamRef = useRef(teamSelectionKey);
  useEffect(() => {
    if (prevTeamRef.current !== teamSelectionKey) {
      prevTeamRef.current = teamSelectionKey;
      setSelectedChargers([]);
      setReports([]);
      setError(null);
      setFailedTeams([]);
      // Forget which team the charger list was seeded for. Without this, going
      // Team A → A+B → A leaves the cleared selection unseeded (the seeding
      // effect still thinks it has already run for A) and Generate would refuse
      // with "select at least one charger".
      initedTeamRef.current = null;
    }
  }, [teamSelectionKey]);

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear; y >= currentYear - 5; y--) years.push(y);
    return years;
  }, [currentYear]);

  const monthOptions = useMemo(() => {
    const opts = MONTHS.map((label, i) => ({ value: i + 1, label }));
    // Only past/current-completed months for the current year.
    return year === currentYear ? opts.filter((m) => m.value <= currentMonth) : opts;
  }, [year, currentYear, currentMonth]);

  const periodLabel = month ? `${MONTHS[month - 1]} ${year}` : "";
  const allSelected =
    chargers.length > 0 && selectedChargers.length === chargers.length;

  const handleChargerChange = (e) => {
    const val = e.target.value;
    setSelectedChargers(typeof val === "string" ? val.split(",") : val);
    setReports([]);
  };

  // MUI hands back the whole next selection. The sentinel row is a toggle rather
  // than a value, so pull it out and either take every team or clear the lot.
  const handleTeamChange = (e) => {
    const val = e.target.value;
    const next = typeof val === "string" ? val.split(",") : val;
    if (next.includes(ALL_TEAMS)) {
      setGpTeamIds(
        allTeamsSelected ? [] : (teamOptions || []).map((t) => t.teamId)
      );
    } else {
      setGpTeamIds(next);
    }
    setReports([]);
  };

  const handleGenerate = async () => {
    if (isGrandParent && !selectedTeamIds.length) {
      setError("Please select a company and at least one team.");
      return;
    }
    if (!month) {
      setError("Please select a month.");
      return;
    }
    // The charger filter only applies to a single team; a multi-team run always
    // covers every charger of every selected team.
    if (!isMultiTeam && !selectedChargers.length) {
      setError("Please select at least one charger.");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setFailedTeams([]);
      setReports([]);
      const headers = { Authorization: `Bearer ${Token.get()}` };
      // When every charger is selected, omit the filter (backend defaults to all).
      const idsParam = isMultiTeam || allSelected ? null : selectedChargers;

      const settled = await settleInBatches(
        selectedTeamIds,
        TEAM_FETCH_CONCURRENCY,
        (tid) =>
          axios
            .get(getTeamPayoutReport(tid, year, month, idsParam), { headers })
            .then((res) => {
              if (!res.data?.success) {
                throw new Error(
                  res.data?.message || "Failed to generate payout report."
                );
              }
              return {
                teamId: tid,
                teamName: teamLabel(tid),
                data: res.data.data,
                // Whether this run asked for a subset of chargers. Without it a
                // deliberate filter is indistinguishable from the server dropping
                // chargers on its own, and only the latter is worth warning about.
                filtered: !!idsParam,
              };
            })
      );

      const ok = settled.filter((s) => s.status === "fulfilled").map((s) => s.value);
      const failed = settled
        .map((s, i) => ({ s, tid: selectedTeamIds[i] }))
        .filter(({ s }) => s.status === "rejected")
        .map(({ s, tid }) => ({
          teamId: tid,
          teamName: teamLabel(tid),
          reason:
            s.reason?.response?.data?.message ||
            s.reason?.message ||
            "Request failed",
        }));

      if (!ok.length) {
        // Keep the per-team reasons even when nothing succeeded — otherwise a
        // 10-team run that fails for 10 different reasons shows one of them.
        setFailedTeams(failed);
        setError(
          failed.length > 1
            ? `Failed to generate the payout for all ${failed.length} selected teams.`
            : failed[0]?.reason || "Failed to generate payout report."
        );
        return;
      }
      // Kept as separate reports, in the order the teams were selected — never
      // summed. Each team's payout stands on its own contract.
      setFailedTeams(failed);
      setReports(ok);
      // A few teams open on arrival; a whole company stays collapsed so the
      // browser is not asked to lay out every team's tables at once.
      setExpandedTeams(ok.length <= AUTO_EXPAND_LIMIT ? ok.map((r) => r.teamId) : []);
      setTab("summary");
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to generate payout report."
      );
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------- finalize / override (super admin) --
  const [finalizing, setFinalizing] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [editRow, setEditRow] = useState(null); // charger being overridden
  const [editPayout, setEditPayout] = useState("");
  const [editFee, setEditFee] = useState("");
  const [editNote, setEditNote] = useState("");
  const [savingOverride, setSavingOverride] = useState(false);

  const handleFinalize = async () => {
    if (!canEditThisReport) return;
    try {
      setFinalizing(true);
      setError(null);
      const token = Token.get();
      await axios.post(
        finalizePayoutUrl(resolvedTeamId, year, month),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await handleGenerate(); // refetch — now served from the frozen snapshot
    } catch (e) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to finalize payout."
      );
    } finally {
      setFinalizing(false);
    }
  };

  // Throw away the frozen month so the report goes back to a live recompute
  // against the current finance config, then regenerate it. Re-finalize keeps
  // per-charger overrides by design, so this is the only way to rebuild a month
  // that was frozen against stale config — and it takes those overrides with it,
  // which is why it asks first.
  const handleDiscardSnapshot = async () => {
    if (!canEditThisReport) return;
    try {
      setDiscarding(true);
      setError(null);
      const res = await axios.delete(
        finalizePayoutUrl(resolvedTeamId, year, month),
        { headers: { Authorization: `Bearer ${Token.get()}` } }
      );
      setDiscardOpen(false);
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: res.data?.message || "Frozen payout discarded.",
          severity: "success",
        })
      );
      await handleGenerate(); // refetch — now a live recompute
    } catch (e) {
      // Close the dialog too: the error Alert renders in the Card behind it, so
      // leaving it open shows a reset button and no explanation.
      setDiscardOpen(false);
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to discard the frozen payout."
      );
    } finally {
      setDiscarding(false);
    }
  };

  const openEdit = (cg) => {
    setEditRow(cg);
    setEditPayout(String(cg.payout?.payout ?? ""));
    setEditFee(String(cg.payout?.amikFee ?? ""));
    setEditNote(cg.overrideNote || "");
  };

  // Which fields the admin actually changed from the finalized values — only those
  // are sent, so an unchanged field is never submitted as an "override".
  const overrideDelta = () => {
    if (!editRow) return { payout: null, amikFee: null, changed: false };
    const op = Number(editRow.payout?.payout);
    const of = Number(editRow.payout?.amikFee);
    const np = editPayout === "" ? null : Number(editPayout);
    const nf = editFee === "" ? null : Number(editFee);
    const payoutChanged = np !== null && !Number.isNaN(np) && np !== op;
    const feeChanged = nf !== null && !Number.isNaN(nf) && nf !== of;
    return {
      payout: payoutChanged ? np : null,
      amikFee: feeChanged ? nf : null,
      changed: payoutChanged || feeChanged,
    };
  };

  const handleSaveOverride = async () => {
    if (!canEditThisReport || !editRow) return;
    const delta = overrideDelta();
    if (!delta.changed) return;
    try {
      setSavingOverride(true);
      setError(null);
      const token = Token.get();
      await axios.put(
        overridePayoutUrl(resolvedTeamId, year, month, editRow.chargepointPk),
        { payout: delta.payout, amikFee: delta.amikFee, note: editNote || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditRow(null);
      await handleGenerate(); // refetch to show overridden totals
    } catch (e) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to override payout."
      );
    } finally {
      setSavingOverride(false);
    }
  };

  // ----------------------------------------- email / schedule (super admin) --
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailPdf, setEmailPdf] = useState(true);
  const [emailExcel, setEmailExcel] = useState(true);
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const [schedOpen, setSchedOpen] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [schedMsg, setSchedMsg] = useState(null);
  const [defaultRecipient, setDefaultRecipient] = useState("");
  const [schedForm, setSchedForm] = useState({
    type: "MONTHLY",
    recipients: "",
    dayOfMonth: 2,
    sendAt: "",
    targetYear: defaultYear,
    targetMonth: defaultMonth,
    attachPdf: true,
    attachExcel: true,
  });
  const [schedBusy, setSchedBusy] = useState(false);

  const authGet = () => ({ headers: { Authorization: `Bearer ${Token.get()}` } });

  const fetchSchedules = async () => {
    const res = await axios.get(payoutEmailSchedulesUrl(resolvedTeamId), authGet());
    const d = res.data?.data || {};
    setSchedules(d.schedules || []);
    setDefaultRecipient(d.defaultRecipient || "");
    return d.defaultRecipient || "";
  };

  const openEmailDialog = async () => {
    setEmailResult(null);
    setEmailSubject("");
    setEmailMessage("");
    setEmailPdf(true);
    setEmailExcel(true);
    try {
      const def = await fetchSchedules();
      setEmailRecipients(def || "");
    } catch (e) {
      setEmailRecipients("");
    }
    setEmailOpen(true);
  };

  const handleSendEmail = async () => {
    if (!canEditThisReport) return;
    try {
      setEmailSending(true);
      setEmailResult(null);
      const idsParam = allSelected ? [] : selectedChargers;
      const res = await axios.post(
        emailPayoutUrl(resolvedTeamId, year, month),
        {
          recipients: emailRecipients,
          chargerIds: idsParam,
          subject: emailSubject || undefined,
          message: emailMessage || undefined,
          attachPdf: emailPdf,
          attachExcel: emailExcel,
        },
        authGet()
      );
      setEmailResult({ ok: true, msg: res.data?.message || "Emailed." });
    } catch (e) {
      setEmailResult({
        ok: false,
        msg: e?.response?.data?.message || e?.message || "Failed to send.",
      });
    } finally {
      setEmailSending(false);
    }
  };

  const openSchedules = async () => {
    setSchedMsg(null);
    setSchedForm((f) => ({ ...f, targetYear: year, targetMonth: month }));
    try {
      const def = await fetchSchedules();
      setSchedForm((f) => ({ ...f, recipients: def || "" }));
    } catch (e) {
      /* ignore */
    }
    setSchedOpen(true);
  };

  const handleCreateSchedule = async () => {
    try {
      setSchedBusy(true);
      await axios.post(
        payoutEmailSchedulesUrl(resolvedTeamId),
        {
          type: schedForm.type,
          recipients: schedForm.recipients,
          dayOfMonth: schedForm.dayOfMonth,
          sendAt: schedForm.sendAt
            ? schedForm.sendAt.replace("T", " ") + ":00"
            : undefined,
          targetYear: schedForm.targetYear,
          targetMonth: schedForm.targetMonth,
          attachPdf: schedForm.attachPdf,
          attachExcel: schedForm.attachExcel,
        },
        authGet()
      );
      await fetchSchedules();
      setSchedForm((f) => ({ ...f, sendAt: "" }));
      setSchedMsg({ ok: true, msg: "Schedule added." });
    } catch (e) {
      setSchedMsg({
        ok: false,
        msg: e?.response?.data?.message || e?.message || "Failed to save schedule.",
      });
    } finally {
      setSchedBusy(false);
    }
  };

  const handleDeleteSchedule = async (id) => {
    try {
      await axios.delete(payoutEmailScheduleUrl(resolvedTeamId, id), authGet());
      await fetchSchedules();
      setSchedMsg({ ok: true, msg: "Schedule deleted." });
    } catch (e) {
      setSchedMsg({
        ok: false,
        msg: e?.response?.data?.message || e?.message || "Failed to delete schedule.",
      });
    }
  };

  const handleToggleSchedule = async (row) => {
    try {
      await axios.put(
        payoutEmailScheduleUrl(resolvedTeamId, row.schedule_pk),
        { active: row.active ? false : true },
        authGet()
      );
      await fetchSchedules();
    } catch (e) {
      setSchedMsg({
        ok: false,
        msg: e?.response?.data?.message || e?.message || "Failed to update schedule.",
      });
    }
  };

  // byPaymentType is a parameter, not a closure over `data`: `data` is null
  // whenever several teams are on screen, and reading it there silently dropped
  // the per-type "Less: RFID / Less: OCPI" bridge lines from every team's Payout
  // Summary — on screen, in Excel and in the PDF — falling back to the lumped
  // legacy line.
  const payoutRows = (summary, payout, meta, transactions, byPaymentType) =>
    buildPayoutSummaryRows(
      summary,
      payout,
      meta,
      brand,
      sumOverDelivered(transactions),
      byPaymentType
    );

  // One "Charging Amount Collected by Payment Type"-style card. Used to render the
  // collected types, RFID, and OCPI as three separate sections, each with its own
  // total so the settled-outside energy is never mixed into the collected figures.
  const renderPaymentTypeSection = (
    title,
    rows,
    caption,
    emptyText,
    firstColLabel = "Payment Type",
    keyId
  ) => {
    const txns = rows.reduce((a, p) => a + (Number(p.transactions) || 0), 0);
    const charging = rows.reduce(
      (a, p) => a + (Number(p.totalChargingCost) || 0),
      0
    );
    return (
      <Card key={keyId} sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2">{title}</Typography>
        {caption && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: "block" }}
          >
            {caption}
          </Typography>
        )}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{firstColLabel}</TableCell>
                <TableCell align="right">Transactions</TableCell>
                <TableCell align="right">Charging Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length ? (
                rows.map((p) => (
                  <TableRow key={p.paymentType}>
                    <TableCell>{p.paymentType}</TableCell>
                    <TableCell align="right">{p.transactions}</TableCell>
                    <TableCell align="right">
                      {withComma(p.totalChargingCost)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="caption" color="text.secondary">
                      {emptyText || "No transactions."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              <TableRow sx={{ "& td": { fontWeight: 700 } }}>
                <TableCell>Total</TableCell>
                <TableCell align="right">{txns}</TableCell>
                <TableCell align="right">{withComma(charging)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    );
  };

  // ------------------------------------------------------------- downloads --
  const buildSummarySheet = (d, teamName) => {
    const {
      summary,
      payout,
      chargers: rows,
      meta,
      transactions,
      byPaymentType,
    } = d;
    const cur = meta?.currency || "INR";
    const grid = [];
    const merges = [];
    const styleMap = {};
    const ensure = (r) => {
      while (grid.length <= r) grid.push([]);
      return grid[r];
    };
    const put = (r, c, v, s) => {
      const row = ensure(r);
      while (row.length <= c) row.push("");
      row[c] = v;
      if (s) styleMap[`${r},${c}`] = s;
    };
    const band = (r, label, s, width) => {
      put(r, 0, label, s);
      for (let c = 1; c < width; c++) put(r, c, "", s);
      merges.push({ s: { r, c: 0 }, e: { r, c: width - 1 } });
    };
    const WIDTH = 8;

    let r = 0;
    band(r, "PAYOUT REPORT", S.title, WIDTH);
    r += 1;
    if (teamName) {
      band(r, teamName, S.subtitle, WIDTH);
      r += 1;
    }
    band(
      r,
      `Period: ${periodLabel}    |    Currency: ${cur}    |    Chargers: ${
        meta?.selectedChargers ?? rows.length
      } of ${meta?.totalChargers ?? rows.length}    |    Generated: ${new Date().toLocaleDateString(
        "en-IN"
      )}`,
      S.meta,
      WIDTH
    );
    r += 2;

    // Charging Summary (team totals).
    band(r, "CHARGING SUMMARY (ALL SELECTED CHARGERS)", S.section, WIDTH);
    r += 1;
    buildChargingSummary(summary, { includePrice: false, byPaymentType, payout }).forEach((m) => {
      put(r, 0, m.label, S.label);
      put(
        r,
        1,
        Number(m.value) || 0,
        m.int ? S.number : m.bold ? S.moneyBold : S.money
      );
      r += 1;
    });
    r += 1;

    // Payout Summary (with/without GST).
    band(r, "PAYOUT SUMMARY", S.section, WIDTH);
    r += 1;
    put(r, 0, "Item", S.header);
    put(r, 1, "Without GST", S.header);
    put(r, 2, "With GST", S.header);
    r += 1;
    payoutRows(summary, payout, meta, transactions, byPaymentType).forEach((pr) => {
      put(r, 0, pr.label, pr.highlight ? S.labelBold : S.label);
      put(r, 1, Number(pr.woGst) || 0, pr.highlight ? S.moneyBold : S.money);
      put(r, 2, Number(pr.wGst) || 0, pr.highlight ? S.highlight : S.moneyBold);
      r += 1;
    });
    put(r, 0, `Payout = Total Collection By Amik − ${brand} CMS Fee`, S.note);
    r += 2;

    // Per-charger breakdown.
    band(r, "PER-CHARGER PAYOUT", S.section, WIDTH);
    r += 1;
    [
      "Charger",
      "Type",
      "Transactions",
      "kWh",
      "Avg Price/kWh (incl. GST)",
      "Charging Amount Collected",
      `${brand} CMS Fee`,
      "Payout",
    ].forEach((h, c) => put(r, c, h, S.header));
    r += 1;
    rows.forEach((cg) => {
      put(r, 0, cg.chargerId, S.label);
      put(r, 1, cg.chargerType || "", S.label);
      put(r, 2, Number(cg.summary.totalTransactions) || 0, S.number);
      put(r, 3, Number(cg.summary.totalKWh) || 0, S.money);
      put(r, 4, Number(cg.summary.avgPricePerKw) || 0, S.money);
      put(r, 5, Number(cg.summary.totalChargingCost) || 0, S.money);
      put(r, 6, Number(cg.payout.amikFee) || 0, S.money);
      put(r, 7, Number(cg.payout.payout) || 0, S.moneyBold);
      r += 1;
    });
    // Total row (price omitted — it differs per charger).
    put(r, 0, "Total", S.labelBold);
    put(r, 1, "", S.labelBold);
    put(r, 2, Number(summary.totalTransactions) || 0, S.number);
    put(r, 3, Number(summary.totalKWh) || 0, S.moneyBold);
    put(r, 4, "", S.labelBold);
    put(r, 5, Number(summary.totalChargingCost) || 0, S.moneyBold);
    put(r, 6, Number(payout.amikFee) || 0, S.moneyBold);
    put(r, 7, Number(payout.payout) || 0, S.highlight);
    r += 2;

    // Charging by settlement bucket: collected (UPI/CARD/…), RFID, and OCPI
    // segregated per roaming company — one block each, matching the on-screen view.
    buildPaymentTypeSections(byPaymentType, transactions).forEach((sec) => {
      band(r, sec.title.toUpperCase(), S.section, WIDTH);
      r += 1;
      [sec.firstColLabel, "Transactions", "Charging Amount Collected"].forEach(
        (h, c) => put(r, c, h, S.header)
      );
      r += 1;
      let secTxns = 0;
      let secCharging = 0;
      if (sec.rows.length) {
        sec.rows.forEach((p) => {
          put(r, 0, p.paymentType, S.label);
          put(r, 1, Number(p.transactions) || 0, S.number);
          put(r, 2, Number(p.totalChargingCost) || 0, S.money);
          secTxns += Number(p.transactions) || 0;
          secCharging += Number(p.totalChargingCost) || 0;
          r += 1;
        });
      } else {
        put(r, 0, sec.emptyText, S.label);
        r += 1;
      }
      put(r, 0, "Total", S.labelBold);
      put(r, 1, secTxns, S.number);
      put(r, 2, secCharging, S.moneyBold);
      r += 2;
    });

    put(r, 0, `Payout rule: ${formatPayoutRule(meta)}`, S.note);
    r += 1;
    put(
      r,
      0,
      "Total Collection By Amik includes GST and covers all payment types. Price per kWh is GST-inclusive.",
      S.note
    );

    const ws = XLSX.utils.aoa_to_sheet(grid);
    Object.entries(styleMap).forEach(([key, s]) => {
      const [rr, cc] = key.split(",").map(Number);
      const addr = XLSX.utils.encode_cell({ r: rr, c: cc });
      if (!ws[addr]) ws[addr] = { t: "s", v: "" };
      ws[addr].s = s;
    });
    ws["!merges"] = merges;
    ws["!cols"] = [
      { wch: 26 },
      { wch: 8 },
      { wch: 14 },
      { wch: 12 },
      { wch: 20 },
      { wch: 24 },
      { wch: 18 },
      { wch: 16 },
    ];
    return ws;
  };

  // Transaction Details sheet. Only the header is styled (per-cell styling of
  // thousands of rows would be slow); money/energy values stay as numbers.
  const buildTransactionsSheet = (d) => {
    const txns = d.transactions || [];
    const header = TXN_COLUMNS.map((c) => c.label);
    const body = txns.map((t) =>
      TXN_COLUMNS.map((c) => {
        const v = t[c.key];
        if (c.time) return fmtTime(v);
        if (c.blankZero && !Number(v)) return "";
        if (c.money || c.num) return Number(v) || 0;
        if (c.key === "paymentType" && t.ocpiCompany)
          return `${t.paymentType} · ${t.ocpiCompany}`;
        return v ?? "";
      })
    );
    const sheet = XLSX.utils.aoa_to_sheet([header, ...body]);
    header.forEach((_, c) => {
      const addr = XLSX.utils.encode_cell({ r: 0, c });
      if (sheet[addr]) sheet[addr].s = S.header;
    });
    sheet["!cols"] = TXN_COLUMNS.map((c) => ({ wch: c.time ? 20 : 15 }));
    return sheet;
  };

  // Excel caps a sheet name at 31 chars, forbids : \ / ? * [ ] and needs it to be
  // unique in the book. The team name is truncated to leave room for the prefix
  // and suffix rather than slicing the finished string — slicing last cut the
  // suffix off a long name, so a team's "Payout" and "Txns" sheets collapsed to
  // the same name and book_append_sheet threw.
  const sheetName = (teamName, i, suffix) => {
    const prefix = `${i + 1}-`;
    const room = 31 - prefix.length - suffix.length - 1; // -1 for the space
    const clean = String(teamName).replace(/[:\\/?*[\]]/g, " ").slice(0, room);
    return `${prefix}${clean} ${suffix}`;
  };

  // Leading "Company Total" sheet for a multi-team download: one row per team plus
  // a TOTAL row, mirroring the card on screen.
  const buildCompanyTotalSheet = () => {
    const t = companyTotals;
    const head = ["Team", "Payout rule", "Chargers", "Txns", "kWh",
      "Charging Collected", "Collection By Amik", `${brand} CMS Fee`, "Payout"];
    const rows = reports.map((rep) => [
      rep.teamName,
      formatPayoutRule(rep.data?.meta),
      rep.data?.chargers?.length || 0,
      Number(rep.data?.summary?.totalTransactions) || 0,
      Number(rep.data?.summary?.totalKWh) || 0,
      Number(rep.data?.summary?.totalChargingCost) || 0,
      Number(rep.data?.payout?.totalCollectionByAmik) || 0,
      Number(rep.data?.payout?.amikFee) || 0,
      Number(rep.data?.payout?.payout) || 0,
    ]);
    // The caveats shown on screen have to travel with the file — this sheet is
    // what gets emailed to finance, and a total that silently adds across
    // currencies or silently omits a team is worse on paper than on screen.
    const caveats = [];
    if (t.mixedCurrency) {
      caveats.push([
        "WARNING: the selected teams do not all use the same currency — this total adds amounts that are not comparable.",
      ]);
    }
    if (failedTeams.length) {
      caveats.push([
        `WARNING: not included — ${failedTeams
          .map((f) => `${f.teamName} (${f.reason})`)
          .join("; ")}`,
      ]);
    }
    const grid = [
      [`Company Total — ${periodLabel}`],
      [`${t.teams} teams · ${t.chargers} chargers · currency ${t.currency}`],
      ["Each team keeps its own payout rule; this sheet only sums money and counts."],
      ...caveats,
      [],
      head,
      ...rows,
      ["TOTAL", "", t.chargers, t.totalTransactions, t.totalKWh, t.totalChargingCost,
        t.totalCollectionByAmik, t.amikFee, t.payout],
    ];
    const ws = XLSX.utils.aoa_to_sheet(grid);
    ws["!cols"] = head.map((h, i) => ({ wch: i === 1 ? 42 : i === 0 ? 26 : 18 }));
    const style = (r, c, s) => {
      const a = XLSX.utils.encode_cell({ r, c });
      if (ws[a]) ws[a].s = s;
    };
    style(0, 0, S.title);
    style(1, 0, S.meta);
    style(2, 0, S.note);
    // Derived from the grid, not hard-coded: the caveat lines above shift every
    // row below them.
    const headRow = 3 + caveats.length + 1; // title, meta, note, caveats, blank
    caveats.forEach((_, i) => style(3 + i, 0, S.note));
    head.forEach((_, c) => style(headRow, c, S.header));
    // Columns 2 (Chargers) and 3 (Txns) are counts; 4+ are money.
    const bodyStyle = (c) => (c > 3 ? S.money : c > 1 ? S.number : S.label);
    const totalStyle = (c) =>
      c > 3 ? S.highlight : c > 1 ? S.number : S.labelBold;
    rows.forEach((_, i) =>
      head.forEach((_, c) => style(headRow + 1 + i, c, bodyStyle(c)))
    );
    head.forEach((_, c) =>
      style(headRow + 1 + rows.length, c, totalStyle(c))
    );
    return ws;
  };

  const handleDownloadExcel = () => {
    if (!hasReports) return;
    const wb = XLSX.utils.book_new();
    if (isMultiReport) {
      XLSX.utils.book_append_sheet(wb, buildCompanyTotalSheet(), "Company Total");
    }
    reports.forEach((rep, i) => {
      XLSX.utils.book_append_sheet(
        wb,
        buildSummarySheet(rep.data, isMultiReport ? rep.teamName : ""),
        isMultiReport ? sheetName(rep.teamName, i, "Payout") : "Payout"
      );
      XLSX.utils.book_append_sheet(
        wb,
        buildTransactionsSheet(rep.data),
        isMultiReport ? sheetName(rep.teamName, i, "Txns") : "Transaction Details"
      );
    });
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Payout_${periodLabel.replace(" ", "_")}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ---- Amik service-fee Tax Invoice + ledger (super admin) -------------------
  // (auth headers reuse the shared authGet helper defined above)
  const [invoicing, setInvoicing] = useState(false);
  const [savingLedger, setSavingLedger] = useState(false);
  // The saved ledger record for the current team+period (if any) — drives the
  // invoice number used on the PDF and the "Saved / Paid" status shown by the button.
  const [periodInvoice, setPeriodInvoice] = useState(null);

  // Refresh the saved invoice for the selected team+period after a report loads.
  const fetchPeriodInvoice = async () => {
    if (!canEditThisReport || !resolvedTeamId || !month) {
      setPeriodInvoice(null);
      return;
    }
    try {
      const res = await axios.get(
        serviceInvoiceByPeriodUrl(resolvedTeamId, year, month),
        authGet()
      );
      setPeriodInvoice(res.data?.data || null);
    } catch (e) {
      setPeriodInvoice(null);
    }
  };

  const handleDownloadServiceInvoice = async () => {
    if (!canEditThisReport || !data) return;
    try {
      setInvoicing(true);
      setError(null);
      await downloadServiceFeeInvoice(data, {
        periodLabel,
        month,
        year,
        teamId: resolvedTeamId,
        brand,
        invoiceNoOverride: periodInvoice?.invoice_no,
      });
    } catch (e) {
      setError(e?.message || "Failed to generate the service-fee invoice.");
    } finally {
      setInvoicing(false);
    }
  };

  // Super admin: persist this period's invoice to the ledger (upsert). A re-save
  // keeps any edited number and the paid status; only the amounts/buyer refresh.
  const handleSaveToLedger = async () => {
    if (!canEditThisReport || !data) return;
    if (!(Number(data?.payout?.amikFee) > 0)) {
      setError("No Amik service fee for this period — nothing to save.");
      return;
    }
    try {
      setSavingLedger(true);
      setError(null);
      const payload = buildInvoicePayload(data, {
        month,
        year,
        teamId: resolvedTeamId,
        invoiceNoOverride: periodInvoice?.invoice_no,
      });
      const res = await axios.post(serviceInvoicesUrl, payload, authGet());
      setPeriodInvoice(res.data?.data || null);
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: res.data?.message || "Invoice saved to ledger.",
          severity: "success",
        })
      );
      if (tab === "invoices") fetchLedger();
    } catch (e) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to save invoice."
      );
    } finally {
      setSavingLedger(false);
    }
  };

  // ---- Ledger tab (all invoices) ---------------------------------------------
  const [ledgerRows, setLedgerRows] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const fetchLedger = async () => {
    try {
      setLedgerLoading(true);
      const res = await axios.get(serviceInvoicesUrl, authGet());
      setLedgerRows(res.data?.data || []);
    } catch (e) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to load invoices."
      );
    } finally {
      setLedgerLoading(false);
    }
  };

  // Edit invoice number
  const [editNoRow, setEditNoRow] = useState(null);
  const [editNoValue, setEditNoValue] = useState("");
  const [savingNo, setSavingNo] = useState(false);
  const openEditNo = (row) => {
    setEditNoRow(row);
    setEditNoValue(row.invoice_no || "");
  };
  const handleSaveNo = async () => {
    if (!canEdit || !editNoRow) return;
    const next = editNoValue.trim();
    if (!next) return;
    try {
      setSavingNo(true);
      await axios.put(
        serviceInvoiceUrl(editNoRow.invoice_pk),
        { invoiceNo: next },
        authGet()
      );
      setEditNoRow(null);
      await fetchLedger();
      // Keep the payout screen's period record in sync if it's the same row.
      if (periodInvoice?.invoice_pk === editNoRow.invoice_pk) {
        setPeriodInvoice((p) => (p ? { ...p, invoice_no: next } : p));
      }
    } catch (e) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to update number."
      );
    } finally {
      setSavingNo(false);
    }
  };

  // Mark as paid (date + reference + note), or revert to unpaid
  const [paidRow, setPaidRow] = useState(null);
  const [paidForm, setPaidForm] = useState({ paidAt: "", reference: "", note: "" });
  const [savingPaid, setSavingPaid] = useState(false);
  const openMarkPaid = (row) => {
    setPaidRow(row);
    setPaidForm({
      paidAt: row.paid_at ? String(row.paid_at).slice(0, 10) : "",
      reference: row.payment_reference || "",
      note: row.payment_note || "",
    });
  };
  const submitPaid = async (paid) => {
    if (!canEdit || !paidRow) return;
    try {
      setSavingPaid(true);
      await axios.put(
        serviceInvoicePaidUrl(paidRow.invoice_pk),
        paid
          ? {
              paid: true,
              paidAt: paidForm.paidAt || null,
              reference: paidForm.reference || null,
              note: paidForm.note || null,
            }
          : { paid: false },
        authGet()
      );
      setPaidRow(null);
      await fetchLedger();
      if (periodInvoice?.invoice_pk === paidRow.invoice_pk) fetchPeriodInvoice();
    } catch (e) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to update status."
      );
    } finally {
      setSavingPaid(false);
    }
  };

  // Reflect any saved ledger record whenever a fresh report loads — and re-fetch if
  // edit access resolves after the report (canEdit derives from async access hooks,
  // and fetchPeriodInvoice bails while !canEdit). The delete flow is separate from
  // the report refresh, but it must also clear the current-period record when the
  // invoice being viewed is removed.
  const [deleteInvoiceRow, setDeleteInvoiceRow] = useState(null);
  const [deletingInvoice, setDeletingInvoice] = useState(false);
  const submitDeleteInvoice = async () => {
    if (!canEdit || !deleteInvoiceRow) return;
    try {
      setDeletingInvoice(true);
      const res = await axios.delete(
        serviceInvoiceUrl(deleteInvoiceRow.invoice_pk),
        authGet()
      );
      const wasCurrentPeriod =
        periodInvoice?.invoice_pk === deleteInvoiceRow.invoice_pk;
      setDeleteInvoiceRow(null);
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: res.data?.message || "Invoice deleted.",
          severity: "success",
        })
      );
      await fetchLedger();
      // The deleted row may be the one backing this period's invoice number, so
      // drop it rather than leaving the button offering to "Update in Ledger".
      if (wasCurrentPeriod) fetchPeriodInvoice();
    } catch (e) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to delete invoice."
      );
      setDeleteInvoiceRow(null);
    } finally {
      setDeletingInvoice(false);
    }
  };

  useEffect(() => {
    if (data) fetchPeriodInvoice();
    else setPeriodInvoice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, canEdit]);

  // Lazy-load the ledger when its (super-admin-only) tab is opened.
  useEffect(() => {
    if (tab === "invoices" && canEdit) fetchLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // One team's pages inside a shared jsPDF document, so a multi-team download
  // is a single file with each team starting on its own page, never blended.
  const renderTeamPdf = (doc, d, logo, teamName) => {
    const {
      summary,
      payout,
      chargers: rows,
      meta,
      transactions,
      byPaymentType,
    } = d;
    const cur = meta?.currency || "INR";
    const pageWidth = doc.internal.pageSize.getWidth();

    if (logo?.dataUrl) {
      const w = 34;
      const h = Math.max(10, (logo.height / logo.width) * w);
      try {
        doc.addImage(logo.dataUrl, "PNG", 14, 10, w, h);
      } catch (e) {
        /* logo optional */
      }
    }
    doc.setFontSize(16);
    doc.setTextColor(31, 56, 100);
    doc.text("Payout Report", pageWidth - 14, 18, { align: "right" });
    doc.setFontSize(10);
    doc.setTextColor(80);
    doc.text(brand, pageWidth - 14, 25, { align: "right" });
    if (teamName) {
      doc.setFontSize(11);
      doc.setTextColor(31, 56, 100);
      doc.text(teamName, 14, 32);
    }

    doc.setFontSize(10);
    doc.setTextColor(40);
    const y = 40;
    doc.text(`Period: ${periodLabel}`, 14, y);
    doc.text(
      `Chargers: ${meta?.selectedChargers ?? rows.length} of ${
        meta?.totalChargers ?? rows.length
      }`,
      14,
      y + 6
    );
    doc.text(`Generated: ${new Date().toLocaleDateString("en-IN")}`, 14, y + 12);
    doc.text(`Currency: ${cur}`, 14, y + 18);

    autoTable(doc, {
      startY: 64,
      head: [["Charging Summary", cur]],
      body: buildChargingSummary(summary, { includePrice: false, byPaymentType, payout }).map((m) => [
        m.label,
        m.int ? String(m.value) : withComma(m.value),
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [31, 56, 100], halign: "left" },
      columnStyles: { 1: { halign: "right" } },
      theme: "grid",
      margin: { left: 14, right: 14 },
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["Payout Summary", "Without GST", "With GST"]],
      body: payoutRows(summary, payout, meta, transactions, byPaymentType).map((pr) => [
        pr.label,
        withComma(pr.woGst),
        withComma(pr.wGst),
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [31, 56, 100], halign: "left" },
      columnStyles: {
        1: { halign: "right" },
        2: { halign: "right", fontStyle: "bold" },
      },
      theme: "grid",
      margin: { left: 14, right: 14 },
      didParseCell: (d) => {
        if (d.section === "body" && d.row.raw[0] === "Payout") {
          d.cell.styles.fillColor = [255, 242, 168];
          d.cell.styles.fontStyle = "bold";
        }
      },
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [
        [
          "Charger",
          "Type",
          "Txns",
          "kWh",
          "Price/kWh (incl. GST)",
          "Charging Amount Collected",
          `${brand} CMS Fee`,
          "Payout",
        ],
      ],
      body: rows.map((cg) => [
        cg.chargerId,
        cg.chargerType || "",
        cg.summary.totalTransactions,
        withComma(cg.summary.totalKWh),
        withComma(cg.summary.avgPricePerKw),
        withComma(cg.summary.totalChargingCost),
        withComma(cg.payout.amikFee),
        withComma(cg.payout.payout),
      ]),
      foot: [
        [
          "Total",
          "",
          summary.totalTransactions,
          withComma(summary.totalKWh),
          "",
          withComma(summary.totalChargingCost),
          withComma(payout.amikFee),
          withComma(payout.payout),
        ],
      ],
      styles: { fontSize: 8 },
      headStyles: { fillColor: [31, 56, 100] },
      footStyles: { fillColor: [234, 240, 248], textColor: 20, fontStyle: "bold" },
      columnStyles: {
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
      },
      theme: "grid",
      margin: { left: 14, right: 14 },
    });

    // Charging by settlement bucket: collected, RFID, and OCPI per roaming company
    // — one table each, matching the on-screen view.
    buildPaymentTypeSections(byPaymentType, transactions).forEach((sec) => {
      const secTxns = sec.rows.reduce(
        (a, p) => a + (Number(p.transactions) || 0),
        0
      );
      const secCharging = sec.rows.reduce(
        (a, p) => a + (Number(p.totalChargingCost) || 0),
        0
      );
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 8,
        head: [[sec.firstColLabel, "Transactions", "Charging Amount Collected"]],
        body: sec.rows.length
          ? sec.rows.map((p) => [
              p.paymentType,
              p.transactions,
              withComma(p.totalChargingCost),
            ])
          : [[sec.emptyText, "", ""]],
        foot: [["Total", secTxns, withComma(secCharging)]],
        styles: { fontSize: 9 },
        headStyles: { fillColor: [31, 56, 100] },
        footStyles: {
          fillColor: [234, 240, 248],
          textColor: 20,
          fontStyle: "bold",
        },
        columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } },
        theme: "grid",
        margin: { left: 14, right: 14 },
      });
    });

    doc.setFontSize(8);
    doc.setTextColor(120);
    const noteY = doc.lastAutoTable.finalY + 8;
    doc.text(`Payout rule: ${formatPayoutRule(meta)}`, 14, noteY);
    doc.text(
      `Payout = Total Collection By Amik - ${brand} CMS Fee. Total Collection By Amik includes GST and covers all payment types.`,
      14,
      noteY + 5,
      { maxWidth: pageWidth - 28 }
    );

    // Transaction Details on a separate (landscape) page.
    if (d.transactions?.length) {
      doc.addPage("a4", "landscape");
      doc.setFontSize(13);
      doc.setTextColor(31, 56, 100);
      doc.text(`Transaction Details (${d.transactions.length})`, 12, 14);
      autoTable(doc, {
        startY: 20,
        head: [TXN_COLUMNS.map((c) => c.label)],
        body: d.transactions.map((t) =>
          TXN_COLUMNS.map((c) => {
            const v = t[c.key];
            if (c.time) return fmtTime(v);
            if (c.blankZero && !Number(v)) return "";
            if (c.money || c.num) return withComma(v);
            if (c.key === "paymentType" && t.ocpiCompany)
              return `${t.paymentType} · ${t.ocpiCompany}`;
            return v ?? "";
          })
        ),
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: { fillColor: [31, 56, 100] },
        columnStyles: {
          7: { halign: "right" },
          8: { halign: "right" },
          9: { halign: "right" },
          10: { halign: "right" },
          11: { halign: "right" },
          12: { halign: "right" },
          13: { halign: "right" },
          14: { halign: "right" },
        },
        theme: "grid",
        margin: { left: 8, right: 8 },
        didParseCell: (dd) => {
          if (dd.section === "body" && d.transactions[dd.row.index]?.overDelivered) {
            dd.cell.styles.fillColor = [255, 235, 200];
          }
        },
      });
    }

  };

  // Leading "Company Total" page for a multi-team PDF, matching the screen card.
  const renderCompanyTotalPdf = (doc) => {
    const t = companyTotals;
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFontSize(16);
    doc.setTextColor(31, 56, 100);
    doc.text("Company Total", 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(80);
    doc.text(brand, pageWidth - 14, 20, { align: "right" });
    doc.setTextColor(40);
    doc.text(`Period: ${periodLabel}`, 14, 30);
    doc.text(
      `${t.teams} teams · ${t.chargers} chargers · currency ${t.currency}`,
      14,
      36
    );
    autoTable(doc, {
      startY: 44,
      head: [["Team", "Chargers", "Txns", "kWh", "Charging Collected",
        "Collection By Amik", `${brand} CMS Fee`, "Payout"]],
      body: [
        ...reports.map((rep) => [
          rep.teamName,
          rep.data?.chargers?.length || 0,
          rep.data?.summary?.totalTransactions ?? 0,
          withComma(rep.data?.summary?.totalKWh),
          withComma(rep.data?.summary?.totalChargingCost),
          withComma(rep.data?.payout?.totalCollectionByAmik),
          withComma(rep.data?.payout?.amikFee),
          withComma(rep.data?.payout?.payout),
        ]),
        ["TOTAL", t.chargers, t.totalTransactions, withComma(t.totalKWh),
          withComma(t.totalChargingCost), withComma(t.totalCollectionByAmik),
          withComma(t.amikFee), withComma(t.payout)],
      ],
      styles: { fontSize: 8 },
      headStyles: { fillColor: [31, 56, 100] },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" },
        4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" } },
      didParseCell: (dd) => {
        if (dd.section === "body" && dd.row.index === reports.length) {
          dd.cell.styles.fontStyle = "bold";
          dd.cell.styles.fillColor = [255, 242, 168];
        }
      },
    });
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(
      "Each team keeps its own payout rule and rates; this page only sums money and counts. Settle each team against its own section.",
      14,
      doc.lastAutoTable.finalY + 8,
      { maxWidth: pageWidth - 28 }
    );
    // The same caveats the screen shows. A printed total that silently adds
    // across currencies, or silently omits a team, is worse than one on screen.
    const notes = [];
    if (t.mixedCurrency) {
      notes.push(
        "WARNING: the selected teams do not all use the same currency — this total adds amounts that are not comparable."
      );
    }
    if (failedTeams.length) {
      notes.push(
        `WARNING: not included — ${failedTeams
          .map((f) => `${f.teamName} (${f.reason})`)
          .join("; ")}`
      );
    }
    if (notes.length) {
      doc.setTextColor(180, 60, 60);
      doc.text(notes, 14, doc.lastAutoTable.finalY + 18, {
        maxWidth: pageWidth - 28,
      });
    }
  };

  const handleDownloadPDF = async () => {
    if (!hasReports) return;
    const doc = new jsPDF();
    const logo = await loadLogoDataUrl();
    if (isMultiReport) {
      renderCompanyTotalPdf(doc);
      doc.addPage("a4", "portrait");
    }
    reports.forEach((rep, i) => {
      // Each team after the first starts a fresh portrait page: the previous
      // team's transaction pages are landscape, so orientation must be reset.
      if (i > 0) doc.addPage("a4", "portrait");
      renderTeamPdf(doc, rep.data, logo, isMultiReport ? rep.teamName : "");
    });
    doc.save(`Payout_${periodLabel.replace(" ", "_")}.pdf`);
  };

  // ---------------------------------------------------------------- render --
  // Company total: one row per team plus a TOTAL row, so the combined figure and
  // the team-wise figures it is made of are readable together. Each team's own
  // payout rule sits on its row — the total deliberately carries no rule of its own.
  const renderCompanyTotal = () => {
    const t = companyTotals;
    if (!t) return null;
    const cols = [
      { label: "Chargers", pick: (r) => r.data?.chargers?.length || 0, int: true },
      { label: "Txns", pick: (r) => r.data?.summary?.totalTransactions, int: true },
      { label: "kWh", pick: (r) => r.data?.summary?.totalKWh },
      { label: "Charging Collected", pick: (r) => r.data?.summary?.totalChargingCost },
      { label: "Collection By Amik", pick: (r) => r.data?.payout?.totalCollectionByAmik },
      { label: `${brand} CMS Fee`, pick: (r) => r.data?.payout?.amikFee },
      { label: "Payout", pick: (r) => r.data?.payout?.payout, bold: true },
    ];
    const totals = [t.chargers, t.totalTransactions, t.totalKWh, t.totalChargingCost,
      t.totalCollectionByAmik, t.amikFee, t.payout];
    return (
      <Card sx={{ p: 2, mb: 4 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Company Total — {periodLabel} ({t.teams} teams, {t.chargers} chargers)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
          The sum of the team payouts below. Each team keeps its own payout rule and
          rates, so no combined rule or average price is shown here — settle each
          team against its own section.
        </Typography>
        {t.mixedCurrency && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            The selected teams do not all use the same currency, so these totals add
            amounts that are not comparable. Read the per-team sections instead.
          </Alert>
        )}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Team</TableCell>
                <TableCell>Payout rule</TableCell>
                {cols.map((c) => (
                  <TableCell key={c.label} align="right" sx={{ whiteSpace: "nowrap" }}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((rep) => (
                <TableRow key={rep.teamId}>
                  <TableCell>{rep.teamName}</TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>
                    {formatPayoutRule(rep.data?.meta)}
                  </TableCell>
                  {cols.map((c) => (
                    <TableCell key={c.label} align="right">
                      {c.int ? c.pick(rep) : withComma(c.pick(rep))}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              <TableRow sx={{ bgcolor: "warning.lighter", "& td": { fontWeight: 700 } }}>
                <TableCell>TOTAL</TableCell>
                <TableCell />
                {totals.map((v, i) => (
                  <TableCell key={cols[i].label} align="right">
                    {cols[i].int ? v : withComma(v)}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Total GST collected {withComma(t.totalTaxAmount)} · Consumption cost excl.
          GST {withComma(t.totalConsumptionCost)} · Payout excl. GST{" "}
          {withComma(t.payoutExclGst)} · Currency {t.currency}
        </Typography>
      </Card>
    );
  };

  // One team's Summary tab: charging summary, payout summary, the payment-type
  // sections and the per-charger table. Rendered once per selected team, side by
  // side — the teams are never added together, because each carries its own
  // payout rule, commission rate and GST and a combined figure would belong to no
  // contract.
  const renderTeamSummary = (d, filtered) => (
    <>
          {/* The server drops a charger from the report when its own team has no
              finance config saved (computeTeamPayoutData skips the whole group),
              and a finalized month only reports what its snapshot froze. Either
              way that charger's energy and payout are silently absent from the
              figures below, which is how "Total kWh" stops being every charger's
              consumption. A deliberate charger filter is excluded — that gap is
              the user's own doing. */}
          {!filtered &&
            (d.meta?.totalChargers ?? 0) > (d.chargers?.length ?? 0) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This report covers {d.chargers?.length ?? 0} of{" "}
                {d.meta?.totalChargers} chargers — the rest were left out by the
                server, so their consumption and payout are missing from every
                figure below. Usually the charger's own team has no finance config
                saved, or the month was finalized before those chargers existed.
              </Alert>
            )}
          <Grid container spacing={3} sx={{ mb: 1 }}>
            <Grid item xs={12} md={5}>
              <Card sx={{ p: 2, height: "100%" }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Charging Summary
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      {buildChargingSummary(d.summary, {
                        includePrice: false,
                        byPaymentType: d.byPaymentType,
                        payout: d.payout,
                      }).map((m) => (
                        <TableRow key={m.label}>
                          <TableCell
                            sx={{
                              fontWeight: m.bold ? 700 : 400,
                              color: m.sub ? "text.secondary" : "inherit",
                            }}
                          >
                            {m.label}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: m.bold ? 700 : 400 }}
                          >
                            {m.int ? m.value : withComma(m.value)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>
            <Grid item xs={12} md={7}>
              <Card sx={{ p: 2, height: "100%" }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Payout Summary — {periodLabel}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="right">Without GST</TableCell>
                        <TableCell align="right">With GST</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payoutRows(
                        d.summary,
                        d.payout,
                        d.meta,
                        d.transactions,
                        d.byPaymentType
                      ).map((pr) => (
                        <TableRow
                          key={pr.label}
                          sx={
                            pr.highlight
                              ? {
                                  bgcolor: "warning.lighter",
                                  "& td": { fontWeight: 700 },
                                }
                              : undefined
                          }
                        >
                          <TableCell>{pr.label}</TableCell>
                          <TableCell align="right">
                            {withComma(pr.woGst)}
                          </TableCell>
                          <TableCell align="right">
                            {withComma(pr.wGst)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Payout = Total Collection By Amik − {brand} CMS Fee
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {buildPaymentTypeSections(
            d.byPaymentType,
            d.transactions
          ).map((s) =>
            renderPaymentTypeSection(
              s.title,
              s.rows,
              s.caption,
              s.emptyText,
              s.firstColLabel,
              s.key
            )
          )}

          <Card sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Per-Charger Payout ({d.chargers.length})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Charger</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Txns</TableCell>
                    <TableCell align="right">kWh</TableCell>
                    <TableCell align="right">Price/kWh (incl. GST)</TableCell>
                    <TableCell align="right">Charging Amount Collected</TableCell>
                    <TableCell align="right">{brand} CMS Fee</TableCell>
                    <TableCell align="right">Payout</TableCell>
                    {canEditThisReport && d.meta?.finalized && (
                      <TableCell align="center">Edit</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {d.chargers.map((cg) => (
                    <TableRow
                      // Pooled rows come from several teams, so key on the
                      // globally unique pk rather than the display id.
                      key={cg.chargepointPk}
                      sx={
                        cg.isOverridden
                          ? { bgcolor: "warning.lighter" }
                          : undefined
                      }
                    >
                      <TableCell>{cg.chargerId}</TableCell>
                      <TableCell>{cg.chargerType || "-"}</TableCell>
                      <TableCell align="right">
                        {cg.summary.totalTransactions}
                      </TableCell>
                      <TableCell align="right">
                        {withComma(cg.summary.totalKWh)}
                      </TableCell>
                      <TableCell align="right">
                        {withComma(cg.summary.avgPricePerKw)}
                      </TableCell>
                      <TableCell align="right">
                        {withComma(cg.summary.totalChargingCost)}
                      </TableCell>
                      <TableCell align="right">
                        {cg.isOverridden &&
                        Number(cg.originalAmikFee) !==
                          Number(cg.payout.amikFee) ? (
                          <>
                            <s style={{ color: "#999" }}>
                              {withComma(cg.originalAmikFee)}
                            </s>{" "}
                            <b>{withComma(cg.payout.amikFee)}</b>
                          </>
                        ) : (
                          withComma(cg.payout.amikFee)
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {cg.isOverridden &&
                        Number(cg.originalPayout) !==
                          Number(cg.payout.payout) ? (
                          <Tooltip
                            title={cg.overrideNote || "Overridden by super admin"}
                          >
                            <span>
                              <s style={{ color: "#999", fontWeight: 400 }}>
                                {withComma(cg.originalPayout)}
                              </s>{" "}
                              {withComma(cg.payout.payout)}
                            </span>
                          </Tooltip>
                        ) : (
                          withComma(cg.payout.payout)
                        )}
                      </TableCell>
                      {canEditThisReport && d.meta?.finalized && (
                        <TableCell align="center">
                          <Tooltip title="Override payout / fee">
                            <IconButton
                              size="small"
                              onClick={() => openEdit(cg)}
                            >
                              <EditIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  <TableRow sx={{ "& td": { fontWeight: 700 } }}>
                    <TableCell>Total</TableCell>
                    <TableCell />
                    <TableCell align="right">
                      {d.summary.totalTransactions}
                    </TableCell>
                    <TableCell align="right">
                      {withComma(d.summary.totalKWh)}
                    </TableCell>
                    <TableCell />
                    <TableCell align="right">
                      {withComma(d.summary.totalChargingCost)}
                    </TableCell>
                    <TableCell align="right">
                      {withComma(d.payout.amikFee)}
                    </TableCell>
                    <TableCell align="right">
                      {withComma(d.payout.payout)}
                    </TableCell>
                    {canEditThisReport && d.meta?.finalized && <TableCell />}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {/* A finalized month's snapshot never stored the monthly fixed rates,
                so the API back-fills them from the CURRENT finance config and
                flags it. If the config changed after freezing, the rule reads as
                today's contract beside figures computed under the old one — say
                so rather than letting the two look consistent. */}
            <Tooltip
              title={
                d.meta?.fixedRatesFromCurrentConfig
                  ? "The fixed monthly rates shown come from the current finance config, not from what was frozen. Re-finalize to bring them back in step."
                  : ""
              }
            >
              <Chip
                size="small"
                color={d.meta?.fixedRatesFromCurrentConfig ? "warning" : "default"}
                label={`Payout rule: ${formatPayoutRule(d.meta)}${
                  d.meta?.fixedRatesFromCurrentConfig ? " (current rates)" : ""
                }`}
              />
            </Tooltip>
            <Chip size="small" label={`Period: ${periodLabel}`} />
            <Chip
              size="small"
              label={`Currency: ${d.meta?.currency || "INR"}`}
            />
            {d.meta?.finalized ? (
              <Chip
                size="small"
                color="success"
                icon={<LockIcon />}
                label={
                  d.meta?.finalizedAt
                    ? `Finalized ${fmtTime(d.meta.finalizedAt)}`
                    : "Finalized"
                }
              />
            ) : (
              <Chip size="small" variant="outlined" label="Live (not finalized)" />
            )}
            {d.meta?.overridden && (
              <Chip size="small" color="warning" label="Contains overrides" />
            )}
          </Stack>
    </>
  );

  // One team's transaction list — the same table the single-team view has always used.
  const renderTeamTransactions = (d) => (
            <Card sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Transaction Details ({d.transactions?.length || 0})
              </Typography>
              {(() => {
                const od = (d.transactions || []).filter(
                  (t) => t.overDelivered
                );
                const total = od.reduce(
                  (a, t) => a + (t.overDeliveredAmount || 0),
                  0
                );
                return od.length ? (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {od.length} transaction(s) over-delivered (customer paid less
                    than charging cost, due to tolerance) — total{" "}
                    {withComma(total)} to adjust. Highlighted below with the
                    &quot;Over-delivery (Adj.)&quot; amount.
                  </Alert>
                ) : null;
              })()}
              {(d.transactions?.length || 0) > TXN_SCREEN_LIMIT && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  Showing first {TXN_SCREEN_LIMIT} of{" "}
                  {d.transactions.length}. Download Excel or PDF for the full
                  list.
                </Typography>
              )}
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {TXN_COLUMNS.map((c) => (
                        <TableCell
                          key={c.key}
                          align={c.money || c.num ? "right" : "left"}
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {c.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(d.transactions || [])
                      .slice(0, TXN_SCREEN_LIMIT)
                      .map((t) => (
                        <TableRow
                          key={`${t.chargerId}-${t.transactionId}`}
                          sx={
                            t.overDelivered
                              ? { bgcolor: "warning.lighter" }
                              : undefined
                          }
                        >
                          {TXN_COLUMNS.map((c) => (
                            <TableCell
                              key={c.key}
                              align={c.money || c.num ? "right" : "left"}
                              sx={{ whiteSpace: "nowrap" }}
                            >
                              {c.time
                                ? fmtTime(t[c.key])
                                : c.blankZero && !Number(t[c.key])
                                ? ""
                                : c.money || c.num
                                ? withComma(t[c.key])
                                : c.key === "paymentType" && t.ocpiCompany
                                ? `${t.paymentType} · ${t.ocpiCompany}`
                                : t[c.key] ?? ""}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
  );

  return (
    <Page title="Payout">
      <Box sx={{ px: { xs: 1, md: 2 }, py: 2 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Payout
        </Typography>

        {readOnly && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Read-only view — you can view and download payouts. Editing (finalize,
            per-charger overrides, email/schedule and the invoice ledger) is
            restricted to super admins.
          </Alert>
        )}

        <Card sx={{ p: 3, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a year and month and the chargers to include (all by default),
            then generate the team payout. A session is counted in the month it
            completed.
            {isGrandParent
              ? " Pick several teams — or Select all — to run a whole company at once; each team gets its own section and its own payout. Finalize, email and invoice stay single-team."
              : ""}
          </Typography>
          <Grid container spacing={2} alignItems="center">
            {isGrandParent && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="payout-company">Company</InputLabel>
                    <Select
                      labelId="payout-company"
                      label="Company"
                      value={gpCompany}
                      onChange={(e) => {
                        setGpCompany(e.target.value);
                        setGpTeamIds([]);
                        setReports([]);
                      }}
                    >
                      {(companies || []).map((c) => (
                        <MenuItem key={c.teamId} value={c.teamId}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small" disabled={!gpCompany}>
                    <InputLabel id="payout-team">Teams</InputLabel>
                    <Select
                      labelId="payout-team"
                      label="Teams"
                      multiple
                      value={gpTeamIds}
                      onChange={handleTeamChange}
                      renderValue={(sel) =>
                        allTeamsSelected
                          ? `All teams (${sel.length})`
                          : sel.length === 1
                          ? teamLabel(sel[0])
                          : `${sel.length} teams selected`
                      }
                    >
                      <MenuItem value={ALL_TEAMS}>
                        <Checkbox
                          checked={allTeamsSelected}
                          indeterminate={
                            gpTeamIds.length > 0 && !allTeamsSelected
                          }
                        />
                        <ListItemText
                          primary={
                            allTeamsSelected
                              ? "Clear all"
                              : `Select all teams (${teamOptions.length})`
                          }
                        />
                      </MenuItem>
                      {(teamOptions || []).map((t) => (
                        <MenuItem key={t.teamId} value={t.teamId}>
                          <Checkbox checked={gpTeamIds.includes(t.teamId)} />
                          <ListItemText primary={t.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="payout-year">Year</InputLabel>
                <Select
                  labelId="payout-year"
                  label="Year"
                  value={year}
                  onChange={(e) => {
                    setYear(e.target.value);
                    setMonth("");
                    setReports([]);
                  }}
                >
                  {yearOptions.map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="payout-month">Month</InputLabel>
                <Select
                  labelId="payout-month"
                  label="Month"
                  value={month}
                  onChange={(e) => {
                    setMonth(e.target.value);
                    setReports([]);
                  }}
                >
                  {monthOptions.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              {/* The charger list is per team, so it only makes sense while one
                  team is selected. A multi-team run always covers every charger
                  of every team — say so rather than showing an empty dropdown. */}
              {isMultiTeam ? (
                <TextField
                  fullWidth
                  size="small"
                  disabled
                  label="Chargers"
                  value="All chargers"
                  helperText="Charger filtering applies to a single team only."
                />
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel id="payout-chargers">Chargers</InputLabel>
                  <Select
                    labelId="payout-chargers"
                    label="Chargers"
                    multiple
                    value={selectedChargers}
                    onChange={handleChargerChange}
                    renderValue={(sel) =>
                      allSelected
                        ? `All chargers (${sel.length})`
                        : `${sel.length} selected`
                    }
                  >
                    {chargers.map((c) => (
                      <MenuItem key={c.chargepoint_pk} value={c.chargepoint_pk}>
                        <Checkbox
                          checked={selectedChargers.includes(c.chargepoint_pk)}
                        />
                        <ListItemText
                          primary={c.chargepoint_id}
                          secondary={c.chargepoint_name}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            {/* Full-width row: up to six actions appear here for a super admin,
                which cannot fit in a one-third column without the labels being
                squeezed. Wraps instead of shrinking on narrow screens. */}
            <Grid item xs={12}>
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  flexWrap: "wrap",
                  gap: 1.5,
                  "& .MuiButton-root": { whiteSpace: "nowrap", flexShrink: 0 },
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleGenerate}
                  disabled={
                    loading ||
                    chargersLoading ||
                    !month ||
                    (isGrandParent && !selectedTeamIds.length)
                  }
                >
                  {loading ? <CircularProgress size={22} /> : "Generate Report"}
                </Button>
                {hasReports && (
                  <>
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadExcel}
                    >
                      Excel
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<PictureAsPdfIcon />}
                      onClick={handleDownloadPDF}
                    >
                      PDF
                    </Button>
                    {canEditThisReport && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={
                          invoicing ? (
                            <CircularProgress size={16} />
                          ) : (
                            <ReceiptLongIcon />
                          )
                        }
                        onClick={handleDownloadServiceInvoice}
                        disabled={invoicing}
                      >
                        Service Fee Invoice
                      </Button>
                    )}
                    {canEditThisReport && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={
                          savingLedger ? (
                            <CircularProgress size={16} />
                          ) : (
                            <PostAddIcon />
                          )
                        }
                        onClick={handleSaveToLedger}
                        disabled={savingLedger}
                      >
                        {periodInvoice ? "Update in Ledger" : "Save to Ledger"}
                      </Button>
                    )}
                    {canEditThisReport && (
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={
                          finalizing ? (
                            <CircularProgress size={16} />
                          ) : (
                            <LockIcon />
                          )
                        }
                        onClick={handleFinalize}
                        disabled={finalizing}
                      >
                        {data?.meta?.finalized ? "Re-finalize" : "Finalize month"}
                      </Button>
                    )}
                    {canEditThisReport && data.meta?.finalized && (
                      <Tooltip title="Delete the frozen figures so this month recomputes from the current finance config">
                        <Button
                          variant="outlined"
                          color="warning"
                          startIcon={<LockOpenIcon />}
                          onClick={() => setDiscardOpen(true)}
                          disabled={discarding}
                        >
                          Discard &amp; Regenerate
                        </Button>
                      </Tooltip>
                    )}
                    {canEditThisReport && (
                      <>
                        <Button
                          variant="outlined"
                          color="info"
                          startIcon={<EmailIcon />}
                          onClick={openEmailDialog}
                        >
                          Email
                        </Button>
                        <Button
                          variant="outlined"
                          color="info"
                          startIcon={<ScheduleIcon />}
                          onClick={openSchedules}
                        >
                          Schedules
                        </Button>
                      </>
                    )}
                  </>
                )}
              </Stack>
            </Grid>
          </Grid>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {!!failedTeams.length && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Not included in the totals below —{" "}
              {failedTeams
                .map((f) => `${f.teamName} (${f.reason})`)
                .join("; ")}
            </Alert>
          )}
        </Card>

        {hasReports && (
          <>
            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
              <Tab value="summary" label="Summary" />
              <Tab
                value="transactions"
                label={`Transaction Details (${totalTxnCount})`}
              />
              {canEdit && <Tab value="invoices" label="Invoices (Ledger)" />}
            </Tabs>

            {tab === "summary" && isMultiReport && renderCompanyTotal()}

            {isMultiReport && (
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Button
                  size="small"
                  onClick={() => setExpandedTeams(reports.map((r) => r.teamId))}
                  disabled={expandedTeams.length === reports.length}
                >
                  Expand all
                </Button>
                <Button
                  size="small"
                  onClick={() => setExpandedTeams([])}
                  disabled={!expandedTeams.length}
                >
                  Collapse all
                </Button>
              </Stack>
            )}

            {tab === "summary" &&
              reports.map((rep) =>
                isMultiReport ? (
                  <TeamSection
                    key={rep.teamId}
                    rep={rep}
                    expanded={expandedTeams.includes(rep.teamId)}
                    onToggle={() => toggleTeam(rep.teamId)}
                  >
                    {renderTeamSummary(rep.data, rep.filtered)}
                  </TeamSection>
                ) : (
                  <Box key={rep.teamId}>
                    {renderTeamSummary(rep.data, rep.filtered)}
                  </Box>
                )
              )}

            {tab === "transactions" &&
              reports.map((rep) =>
                isMultiReport ? (
                  <TeamSection
                    key={rep.teamId}
                    rep={rep}
                    expanded={expandedTeams.includes(rep.teamId)}
                    onToggle={() => toggleTeam(rep.teamId)}
                    txnMode
                  >
                    {renderTeamTransactions(rep.data)}
                  </TeamSection>
                ) : (
                  <Box key={rep.teamId}>{renderTeamTransactions(rep.data)}</Box>
                )
              )}

            {tab === "invoices" && canEdit && (
              <Card sx={{ p: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2">
                    Service Fee Invoices — Ledger ({ledgerRows.length})
                  </Typography>
                  <Button
                    size="small"
                    startIcon={
                      ledgerLoading ? (
                        <CircularProgress size={14} />
                      ) : (
                        <DownloadIcon sx={{ transform: "rotate(180deg)" }} />
                      )
                    }
                    onClick={fetchLedger}
                    disabled={ledgerLoading}
                  >
                    Refresh
                  </Button>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  All saved service-fee invoices across teams. Edit the invoice
                  number or record a payment here.
                </Typography>
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice No.</TableCell>
                        <TableCell>Buyer</TableCell>
                        <TableCell>Period</TableCell>
                        <TableCell>Supply</TableCell>
                        <TableCell align="right">Taxable</TableCell>
                        <TableCell align="right">Tax</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Paid On</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ledgerRows.map((r) => (
                        <TableRow key={r.invoice_pk}>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {r.invoice_no}
                          </TableCell>
                          <TableCell>
                            {r.buyer_name || `Team ${r.team_id}`}
                            {r.buyer_gstin ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {r.buyer_gstin}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {MONTHS[r.month - 1]} {r.year}
                          </TableCell>
                          <TableCell>{r.supply_type || "-"}</TableCell>
                          <TableCell align="right">
                            {withComma(r.taxable_value)}
                          </TableCell>
                          <TableCell align="right">
                            {withComma(r.tax_amount)}
                          </TableCell>
                          <TableCell align="right">
                            {withComma(r.total)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={r.status === "PAID" ? "success" : "warning"}
                              variant={r.status === "PAID" ? "filled" : "outlined"}
                              label={r.status === "PAID" ? "Paid" : "Unpaid"}
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {r.paid_at ? String(r.paid_at).slice(0, 10) : "—"}
                            {r.payment_reference ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {r.payment_reference}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            <Tooltip title="Edit invoice number">
                              <IconButton
                                size="small"
                                onClick={() => openEditNo(r)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Button
                              size="small"
                              color={r.status === "PAID" ? "inherit" : "success"}
                              startIcon={<PaidIcon fontSize="small" />}
                              onClick={() => openMarkPaid(r)}
                            >
                              {r.status === "PAID" ? "Payment" : "Mark Paid"}
                            </Button>
                            {/* A paid invoice is a settlement record, so it is
                                not deletable here — mark it unpaid first, which
                                is a deliberate and visible step. */}
                            {r.status !== "PAID" && (
                              <Tooltip title="Delete this unpaid invoice">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setDeleteInvoiceRow(r)}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!ledgerLoading && ledgerRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={10} align="center">
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ py: 2 }}
                            >
                              No invoices saved yet. Generate a report and click
                              &quot;Save to Ledger&quot;.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            )}
          </>
        )}

        {/* Super-admin: delete an unpaid invoice from the ledger */}
        <Dialog
          open={!!deleteInvoiceRow}
          onClose={() => !deletingInvoice && setDeleteInvoiceRow(null)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Delete invoice</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Invoice <strong>{deleteInvoiceRow?.invoice_no}</strong> for{" "}
              {deleteInvoiceRow?.buyer_name ||
                `Team ${deleteInvoiceRow?.team_id}`}{" "}
              ({MONTHS[(deleteInvoiceRow?.month || 1) - 1]}{" "}
              {deleteInvoiceRow?.year}) will be permanently removed from the
              ledger.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Only unpaid invoices can be deleted. You can save this period to the
              ledger again from its payout report, which will issue a new invoice
              number.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteInvoiceRow(null)}
              disabled={deletingInvoice}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={submitDeleteInvoice}
              disabled={deletingInvoice}
              startIcon={
                deletingInvoice ? <CircularProgress size={16} /> : null
              }
            >
              {deletingInvoice ? "Deleting…" : "Delete invoice"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Super-admin: discard the frozen month and recompute it */}
        <Dialog
          open={discardOpen}
          onClose={() => !discarding && setDiscardOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Discard frozen payout — {periodLabel}</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This permanently deletes the frozen figures for {periodLabel}
              {resolvedTeamId && reports[0]?.teamName
                ? ` (${reports[0].teamName})`
                : ""}
              . Any per-charger override on this month is deleted with them and
              cannot be recovered.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              The month then recomputes live from the current finance config and
              transaction data. Use this when a month was finalized against
              settings that have since been corrected — a plain Re-finalize keeps
              existing overrides and so cannot rebuild it from scratch. Finalize
              again afterwards to re-freeze the corrected figures.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDiscardOpen(false)} disabled={discarding}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleDiscardSnapshot}
              disabled={discarding}
              startIcon={discarding ? <CircularProgress size={16} /> : null}
            >
              {discarding ? "Discarding…" : "Discard & Regenerate"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Super-admin: edit invoice number */}
        <Dialog
          open={!!editNoRow}
          onClose={() => setEditNoRow(null)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Edit invoice number</DialogTitle>
          <DialogContent>
            <Typography variant="caption" color="text.secondary">
              {editNoRow
                ? `${editNoRow.buyer_name || `Team ${editNoRow.team_id}`} · ${
                    MONTHS[(editNoRow.month || 1) - 1]
                  } ${editNoRow.year}`
                : ""}
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Invoice No."
              value={editNoValue}
              onChange={(e) => setEditNoValue(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditNoRow(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveNo}
              disabled={savingNo || !editNoValue.trim()}
              startIcon={savingNo ? <CircularProgress size={16} /> : null}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Super-admin: mark invoice paid / unpaid */}
        <Dialog
          open={!!paidRow}
          onClose={() => setPaidRow(null)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>
            {paidRow?.status === "PAID" ? "Payment details" : "Mark as paid"}
          </DialogTitle>
          <DialogContent>
            <Typography variant="caption" color="text.secondary">
              {paidRow
                ? `${paidRow.invoice_no} · ${withComma(paidRow.total)}`
                : ""}
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Payment date"
              InputLabelProps={{ shrink: true }}
              value={paidForm.paidAt}
              onChange={(e) =>
                setPaidForm((f) => ({ ...f, paidAt: e.target.value }))
              }
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Reference / UTR"
              value={paidForm.reference}
              onChange={(e) =>
                setPaidForm((f) => ({ ...f, reference: e.target.value }))
              }
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Note"
              multiline
              minRows={2}
              value={paidForm.note}
              onChange={(e) =>
                setPaidForm((f) => ({ ...f, note: e.target.value }))
              }
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            {paidRow?.status === "PAID" && (
              <Button
                color="warning"
                onClick={() => submitPaid(false)}
                disabled={savingPaid}
              >
                Mark Unpaid
              </Button>
            )}
            <Button onClick={() => setPaidRow(null)}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => submitPaid(true)}
              disabled={savingPaid}
              startIcon={savingPaid ? <CircularProgress size={16} /> : null}
            >
              {paidRow?.status === "PAID" ? "Update" : "Mark Paid"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Super-admin per-charger override dialog */}
        <Dialog
          open={!!editRow}
          onClose={() => setEditRow(null)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Override payout — {editRow?.chargerId}</DialogTitle>
          <DialogContent>
            <Typography variant="caption" color="text.secondary">
              Original payout: {withComma(editRow?.originalPayout)} · fee:{" "}
              {withComma(editRow?.originalAmikFee)}. Leave a field unchanged to keep
              the finalized value.
            </Typography>
            <TextField
              label={`Payout (${data?.meta?.currency || "INR"})`}
              type="number"
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              value={editPayout}
              onChange={(e) => setEditPayout(e.target.value)}
            />
            <TextField
              label={`${brand} CMS Fee (${data?.meta?.currency || "INR"})`}
              type="number"
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              value={editFee}
              onChange={(e) => setEditFee(e.target.value)}
            />
            <TextField
              label="Note (reason for override)"
              fullWidth
              size="small"
              multiline
              minRows={2}
              sx={{ mt: 2 }}
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditRow(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveOverride}
              disabled={savingOverride || !overrideDelta().changed}
            >
              {savingOverride ? <CircularProgress size={20} /> : "Save override"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Email payout to customer */}
        <Dialog open={emailOpen} onClose={() => setEmailOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Email payout — {periodLabel}</DialogTitle>
          <DialogContent>
            <Typography variant="caption" color="text.secondary">
              Sends the {allSelected ? "full team" : `${selectedChargers.length}-charger`} payout as
              PDF/Excel to the recipients. {data?.meta?.finalized ? "Finalized statement." : "Live (not finalized) figures."}
            </Typography>
            <TextField
              label="Recipients (comma-separated)"
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              value={emailRecipients}
              onChange={(e) => setEmailRecipients(e.target.value)}
              helperText={defaultRecipient ? `Team default: ${defaultRecipient}` : "Enter one or more emails"}
            />
            <TextField
              label="Subject (optional)"
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
            />
            <TextField
              label="Message (optional)"
              fullWidth
              size="small"
              multiline
              minRows={2}
              sx={{ mt: 2 }}
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
            />
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <label>
                <Checkbox checked={emailPdf} onChange={(e) => setEmailPdf(e.target.checked)} size="small" />
                PDF
              </label>
              <label>
                <Checkbox checked={emailExcel} onChange={(e) => setEmailExcel(e.target.checked)} size="small" />
                Excel
              </label>
            </Stack>
            {emailResult && (
              <Alert severity={emailResult.ok ? "success" : "error"} sx={{ mt: 1 }}>
                {emailResult.msg}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailOpen(false)}>Close</Button>
            <Button
              variant="contained"
              onClick={handleSendEmail}
              disabled={emailSending || (!emailPdf && !emailExcel)}
            >
              {emailSending ? <CircularProgress size={20} /> : "Send now"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Manage email schedules */}
        <Dialog open={schedOpen} onClose={() => setSchedOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>Payout email schedules</DialogTitle>
          <DialogContent>
            {schedMsg && (
              <Alert severity={schedMsg.ok ? "success" : "error"} sx={{ mb: 1 }}>
                {schedMsg.msg}
              </Alert>
            )}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Existing schedules
            </Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>When</TableCell>
                    <TableCell>Recipients</TableCell>
                    <TableCell>Files</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schedules.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="caption" color="text.secondary">
                          No schedules yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {schedules.map((s) => (
                    <TableRow key={s.schedule_pk}>
                      <TableCell>{s.type}</TableCell>
                      <TableCell>
                        {s.type === "MONTHLY"
                          ? `Day ${s.day_of_month} (prev month)`
                          : `${fmtTime(s.send_at)} → ${s.target_month}/${s.target_year}`}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, wordBreak: "break-all" }}>
                        {s.recipients}
                      </TableCell>
                      <TableCell>
                        {[s.attach_pdf ? "PDF" : null, s.attach_excel ? "Excel" : null]
                          .filter(Boolean)
                          .join(" + ")}
                      </TableCell>
                      <TableCell>
                        {s.active ? (s.type === "ONCE" ? s.status : "Active") : "Paused"}
                      </TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => handleToggleSchedule(s)}>
                          {s.active ? "Pause" : "Resume"}
                        </Button>
                        <Button size="small" color="error" onClick={() => handleDeleteSchedule(s.schedule_pk)}>
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Add schedule
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type"
                    value={schedForm.type}
                    onChange={(e) => setSchedForm((f) => ({ ...f, type: e.target.value }))}
                  >
                    <MenuItem value="MONTHLY">Recurring monthly</MenuItem>
                    <MenuItem value="ONCE">One-off</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {schedForm.type === "MONTHLY" ? (
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Day of month (UTC)"
                    type="number"
                    fullWidth
                    size="small"
                    inputProps={{ min: 1, max: 28 }}
                    value={schedForm.dayOfMonth}
                    onChange={(e) => setSchedForm((f) => ({ ...f, dayOfMonth: e.target.value }))}
                    helperText="Emails the previous month"
                  />
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Send at (UTC)"
                      type="datetime-local"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={schedForm.sendAt}
                      onChange={(e) => setSchedForm((f) => ({ ...f, sendAt: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      label="Year"
                      type="number"
                      fullWidth
                      size="small"
                      value={schedForm.targetYear}
                      onChange={(e) => setSchedForm((f) => ({ ...f, targetYear: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      label="Month"
                      type="number"
                      fullWidth
                      size="small"
                      inputProps={{ min: 1, max: 12 }}
                      value={schedForm.targetMonth}
                      onChange={(e) => setSchedForm((f) => ({ ...f, targetMonth: e.target.value }))}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <TextField
                  label="Recipients (comma-separated)"
                  fullWidth
                  size="small"
                  value={schedForm.recipients}
                  onChange={(e) => setSchedForm((f) => ({ ...f, recipients: e.target.value }))}
                  helperText={defaultRecipient ? `Team default: ${defaultRecipient}` : ""}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <label>
                    <Checkbox
                      size="small"
                      checked={schedForm.attachPdf}
                      onChange={(e) => setSchedForm((f) => ({ ...f, attachPdf: e.target.checked }))}
                    />
                    PDF
                  </label>
                  <label>
                    <Checkbox
                      size="small"
                      checked={schedForm.attachExcel}
                      onChange={(e) => setSchedForm((f) => ({ ...f, attachExcel: e.target.checked }))}
                    />
                    Excel
                  </label>
                  <Button
                    variant="contained"
                    onClick={handleCreateSchedule}
                    disabled={schedBusy || !schedForm.recipients}
                  >
                    {schedBusy ? <CircularProgress size={20} /> : "Add schedule"}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSchedOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
}
